/**
 * Shows the original next to the extracted data, because checking a value means looking at the
 * paper. The stored file is protected in PocketBase, so the URL is fetched with a short-lived
 * token rather than built from the record.
 */
import { useQuery } from "@tanstack/react-query";
import { Download, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getFileUrl, isImage, type DocumentRecord } from "@/lib/api/documents";

export function DocumentPreview({ document }: { document: DocumentRecord }) {
  const { t } = useTranslation();
  const { data: url, isPending } = useQuery({
    queryKey: ["document-file", document.id, document.file],
    queryFn: () => getFileUrl(document),
    // File tokens are short-lived; refetch rather than serve a stale one.
    staleTime: 60_000,
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium">{t("document.original")}</p>
        <span className="truncate text-xs text-muted-foreground">{document.originalName}</span>
        <div className="ml-auto flex gap-1">
          <Button
            asChild
            variant="ghost"
            size="icon"
            disabled={!url}
            aria-label={t("document.openFile")}
          >
            <a href={url ?? "#"} target="_blank" rel="noreferrer">
              <ExternalLink className="size-4" />
            </a>
          </Button>
          <Button
            asChild
            variant="ghost"
            size="icon"
            disabled={!url}
            aria-label={t("document.download")}
          >
            <a href={url ?? "#"} download={document.originalName || document.file}>
              <Download className="size-4" />
            </a>
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border bg-muted/30">
        {isPending && <Skeleton className="h-[28rem] w-full" aria-busy="true" />}
        {url && isImage(document) && (
          <img
            src={url}
            alt={document.originalName || document.docNumber || t("document.original")}
            className="max-h-[36rem] w-full object-contain"
          />
        )}
        {url && !isImage(document) && (
          <iframe
            src={url}
            title={document.originalName || t("document.original")}
            className="h-[36rem] w-full"
            data-testid="pdf-preview"
          />
        )}
      </div>
    </div>
  );
}
